import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Generate time options from 5am to 6pm in hourly increments
 * @returns {string[]} Array of time strings in 12-hour format
 */
export const generateTimeOptions = () => {
  const options = [];
  for (let hour = 5; hour <= 18; hour++) {
    const formattedHour = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    options.push(`${formattedHour}:00 ${ampm}`);
  }
  return options;
};

/**
 * Convert 12-hour time format to 24-hour format for storage
 * @param {string} timeString - Time in 12-hour format (e.g., "9:00 AM")
 * @returns {string} Time in 24-hour format (e.g., "09:00")
 */
export const convertTo24HourFormat = (timeString) => {
  if (!timeString) return '09:00'; // Default fallback
  
  // Parse time like "9:00 AM" or "2:00 PM"
  const match = timeString.match(/(\d+):(\d+)\s+(AM|PM)/i);
  if (!match) return timeString; // Return as-is if doesn't match expected format
  
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = match[3].toUpperCase();
  
  // Convert to 24-hour format
  if (period === 'PM' && hours < 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  
  return `${hours.toString().padStart(2, '0')}:${minutes}`;
};

/**
 * Parse date string and create timezone-safe date for API
 * @param {string} scheduledDate - Date string in YYYY-MM-DD format
 * @returns {string} ISO string formatted for EST/EDT timezone
 */
export const createTimezoneSafeDate = (scheduledDate) => {
  // Parse the date using safer methods to avoid timezone issues
  const [year, month, day] = scheduledDate.split('-').map(Number);
  
  // Create date directly from components to avoid rollover issues
  // This prevents the month rollover bug that occurs when using new Date() + setMonth()
  const parsedDate = new Date(year, month - 1, day, 12, 0, 0, 0);
  
  // Always use 12:00 noon in EST/EDT to avoid any timezone crossing day boundaries
  return formatInTimeZone(
    parsedDate,
    'America/New_York',
    "yyyy-MM-dd'T'12:00:00.SSSXXX"
  );
};

/**
 * Get current date in YYYY-MM-DD format
 * @returns {string} Current date formatted for date input
 */
export const getCurrentDateString = () => {
  return format(new Date(), 'yyyy-MM-dd');
};

/**
 * Get date string for 90 days from now
 * @returns {string} Future date formatted for date input
 */
export const getFutureDateString = (daysFromNow = 90) => {
  const futureDate = new Date(Date.now() + daysFromNow * 24 * 60 * 60 * 1000);
  return futureDate.toISOString().split('T')[0];
}; 